import {
  Box,
  Code,
  Icon,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import React, {
  useRef,
  useCallback,
  useMemo,
  useEffect,
  useState,
} from 'react';
import {
  Edge,
  Node,
  OnConnect,
  OnSelectionChangeFunc,
  useOnSelectionChange,
  useReactFlow,
  XYPosition,
} from 'reactflow';

import { error as commonError } from '@finley-technologies/common';
import { TapeBuilderUI } from '@finley-technologies/tape-builder';

import { QAModeControls } from './calcAgentQA/QAModeControls';
import { CommonNodeDisplayWithHandles } from './commonNodeDisplay';
import {
  getNewNodeParams,
  getVisibleGraph,
  isGraphFiltered,
  OrgSlugToOrgMapType,
  setSelectedNode,
  StoredGraphState,
  TapeBuilderMode,
  useGraph,
} from './GraphContext';
import {
  exportGraph,
  importAndMergeGraph,
  importGraph,
} from './ImportExportGraph';
import { isValidNodeType } from './nodes';
import { SearchOverlay } from './SearchOverlay';
import { TapeBuilderOptionalDrawer } from './TapeBuilderDrawer';
import {
  ReactFlowWrapperControl,
  GraphPositions,
  NodeTypes,
} from 'packages/common-frontend/components/ReactFlow';
import { TextWithCopy } from 'packages/common-frontend/components/TextWithCopy';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { useCopyPaste } from 'packages/tapeBuilder/ui/useCopyPaste';
import { useMultiConnect } from 'packages/tapeBuilder/ui/useMultiConnect';
import { FiFilter } from 'react-icons/fi';

const nodeTypes: NodeTypes = TapeBuilderUI.uiNodes.reduce((acc, node) => {
  return {
    ...acc,
    [node.nodeType]: CommonNodeDisplayWithHandles,
  };
}, {} as NodeTypes);

// this controls allows to visualize tape builder nodes and edges.
type Props = {
  mode: TapeBuilderMode;
  pipelineDefinitionId: string;
  orgSlugMap: OrgSlugToOrgMapType;
  onGraphUploaded: (params: {
    storedGraph: StoredGraphState;
    positions: GraphPositions;
  }) => void;
  handleSavePositions: () => void;
};
export const TapeBuilderControl = ({
  mode,
  pipelineDefinitionId: _pipelineDefinitionId,
  orgSlugMap,
  onGraphUploaded,
  handleSavePositions,
}: Props) => {
  const graphContext = useGraph();
  const toast = useToast();
  const reactFlowInstance = useReactFlow();
  const [selectedEdge, setSelectedEdge] = useState<Edge | null>(null);
  const isMultiSelectKeyPressed = useRef(false);

  // Determine which modifier key to use based on platform
  const isMac = navigator.userAgent.includes('Macintosh');

  // Track Ctrl/Cmd key, and shift key state (for multi-selection)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const modifierPressed = isMac ? event.metaKey : event.ctrlKey;
      const shiftPressed = event.shiftKey;
      if (modifierPressed || shiftPressed) {
        isMultiSelectKeyPressed.current = true;
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      const modifierPressed = isMac ? event.metaKey : event.ctrlKey;
      const shiftPressed = event.shiftKey;
      // Update multi-select state to reflect current key state
      isMultiSelectKeyPressed.current = modifierPressed || shiftPressed;
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [isMac]);

  const onSelectionChage = useCallback<OnSelectionChangeFunc>(
    ({ nodes, edges }) => {
      // This function runs anytime there is a change in the selected nodes or edges
      setSelectedEdge(edges.length === 1 ? edges[0] : null);

      // Don't open drawer if Ctrl/Cmd key is pressed (user is multi-selecting)
      if (isMultiSelectKeyPressed.current) {
        setSelectedNode(graphContext, null);
      } else if (nodes.length === 1) {
        setSelectedNode(graphContext, nodes[0].id);
      } else {
        setSelectedNode(graphContext, null);
      }
    },
    [graphContext],
  );
  useOnSelectionChange({ onChange: onSelectionChage });
  useCopyPaste(graphContext);
  useMultiConnect(graphContext);

  // controls the search/filter overley.
  const { isOpen: searchOpen, onToggle: toggleSearch } = useDisclosure();
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Here we get the nodes and edges that are visible after the filters have
    // been applied. Then we hide the flowNodes and flowEdges accordingly.
    const visibleGraph = getVisibleGraph(graphContext);

    const visibleNodeIDs = new Set(
      visibleGraph.nodes.map((node) => node.metadata.tapeBuilderNodeId),
    );
    const visibleEdgeIDs = new Set(visibleGraph.edges.map((edge) => edge.id));

    reactFlowInstance.setNodes((nodes) =>
      nodes.map((node) => ({ ...node, hidden: !visibleNodeIDs.has(node.id) })),
    );

    reactFlowInstance.setEdges((edges) =>
      edges.map((edge) => ({ ...edge, hidden: !visibleEdgeIDs.has(edge.id) })),
    );
  }, [graphContext, reactFlowInstance]);

  // hookup hotkey ctrl+/ to open the search overlay
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check if Ctrl+/ was pressed
      if ((event.metaKey || event.ctrlKey) && event.key === '/') {
        event.preventDefault(); // Prevent the default action of Ctrl+/
        toggleSearch();
      }
    };

    // Add event listener for keydown
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup the event listener
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [searchOpen, toggleSearch]);

  // triggered by flow wrapper, when a new node is added.
  const onNewNode = (
    nodeType: TapeBuilderUI.TapeBuilderNodeType,
    position: XYPosition,
  ) => {
    if (!isValidNodeType(nodeType)) {
      return;
    }

    const nodeInterface = TapeBuilderUI.getUINodeByType(nodeType);
    const newNode = nodeInterface.createNewNode(getNewNodeParams(graphContext));

    graphContext.dispatch({
      type: 'addNode',
      node: newNode,
    });

    const {
      tapeBuilderNodeId: id,
      tapeBuilderNodeType: type,
    } = newNode.metadata;
    const flowNode = { id, type, data: id, position };
    reactFlowInstance.addNodes(flowNode);
  };

  // triggered by SwitchTapePanel
  const onDeleteNode = useCallback(
    (nodeId: string) => {
      graphContext.dispatch({
        type: 'deleteNode',
        nodeId,
      });

      reactFlowInstance.setNodes((nodes) =>
        nodes.filter((node) => node.id !== nodeId),
      );
    },
    [graphContext, reactFlowInstance],
  );

  const onNodesDeleted = (nodesToDelete: Node[]) => {
    nodesToDelete.forEach((node) => onDeleteNode(node.id));
  };

  // triggered by SwitchTapePanel
  const onNodeConfigChanged = useCallback(
    (nodeId: string, config: object) => {
      graphContext.dispatch({
        type: 'updateNodeConfig',
        nodeId,
        config,
      });
    },
    [graphContext],
  );

  // triggered by ReactFlowWrapperControl
  const onConnect: OnConnect = ({ source, target }) => {
    if (source && target) {
      const id = `${source}:${target}`;
      const edge = reactFlowInstance.getEdge(id);

      // Only add edges if it doesn't already exist. Two nodes can
      // have only one connection between them.
      if (edge === undefined) {
        graphContext.dispatch({
          type: 'addEdge',
          edge: { source, target, id },
        });

        reactFlowInstance.addEdges({ source, target, id });
      }
    }
  };

  // delete edge is also triggered by ReactFlowWrapperControl
  // when a connection is deleted.
  // triggered by ReactFlowWrapperControl
  const onEdgeDeleted = (edgeToDelete: Edge) => {
    graphContext.dispatch({
      type: 'deleteEdge',
      edge: edgeToDelete,
    });

    reactFlowInstance.setEdges((edges) =>
      edges.filter((edge) => edge !== edgeToDelete),
    );
  };

  const onEdgesDeleted = (edgesToDelete: Edge[]) => {
    edgesToDelete.forEach((edge) => onEdgeDeleted(edge));
  };

  const handleExport = () => {
    // save nodes, edges as well as node positions.
    const {
      orgSlug: organizationSlug,
      artifactDocumentId,
    } = graphContext.state;

    const flowNodes = reactFlowInstance
      .getNodes()
      .filter((node) => !node.hidden);

    // export only the visible graph.
    try {
      const storedGraph = getVisibleGraph(graphContext);
      exportGraph({
        storedGraph,
        flowNodes,
        organizationSlug,
        artifactDocumentId,
      });
    } catch (error) {
      const errorMessage = commonError.getErrorMessage(error);
      errorToastConfig.title = errorMessage;
      toast(errorToastConfig);
    }
  };

  const handleImport = async (file: File) => {
    try {
      const { storedGraph, positions } = await importGraph(file);
      onGraphUploaded({ storedGraph, positions });
    } catch (error) {
      const errorMessage = commonError.getErrorMessage(error);
      errorToastConfig.title = errorMessage;
      toast(errorToastConfig);
    }
  };

  const handleImportMerge = async (file: File) => {
    try {
      const { storedGraph, positions } = await importAndMergeGraph(
        file,
        graphContext.state,
      );
      onGraphUploaded({ storedGraph, positions });
    } catch (error) {
      const errorMessage = commonError.getErrorMessage(error);
      errorToastConfig.title = errorMessage;
      toast(errorToastConfig);
    }
  };

  const topRight = useMemo(
    () => (
      <>
        <Icon
          as={FiFilter}
          onClick={toggleSearch}
          color={isGraphFiltered(graphContext) ? 'green.300' : 'white'}
          title="Toggle search (Ctrl + /)" // Tooltip to indicate the hotkey
        />
        <Code ml={2}>
          <TextWithCopy label="Copy ArtifactDocumentId">
            {graphContext.state.artifactDocumentId}
          </TextWithCopy>
        </Code>
      </>
    ),
    [graphContext, toggleSearch],
  );

  const topLeft = mode === 'QA' ? <QAModeControls /> : undefined;
  return (
    <Box>
      <VStack>
        <Box
          ref={elementRef}
          resize="vertical"
          overflow="auto"
          width="100%"
          height="calc(100vh - 300px)"
          minHeight="400px"
          border="1px solid #ccc"
        >
          {searchOpen && <SearchOverlay onClose={() => toggleSearch()} />}
          <ReactFlowWrapperControl
            topRight={topRight}
            topLeft={topLeft}
            elementRef={elementRef}
            nodeTypes={nodeTypes}
            onConnect={mode === 'Edit' ? onConnect : undefined}
            onEdgeDeleted={mode === 'Edit' ? onEdgeDeleted : undefined}
            onEdgesDeleted={mode === 'Edit' ? onEdgesDeleted : undefined}
            onNodesDeleted={mode === 'Edit' ? onNodesDeleted : undefined}
            onNewNode={onNewNode}
            handleExport={handleExport}
            handleImport={handleImport}
            handleImportMerge={handleImportMerge}
            handleSavePositions={handleSavePositions}
            selectedEdge={selectedEdge}
          />
        </Box>
        <TapeBuilderOptionalDrawer
          mode={mode}
          orgSlugMap={orgSlugMap}
          onDelete={onDeleteNode}
          onConfigChange={onNodeConfigChanged}
        />
      </VStack>
    </Box>
  );
};
